import { Barcode } from "@corpuscomplete/core/common/barcode";
import type { UserActor } from "@corpuscomplete/core/actor";
import type { Transaction } from "@rocicorp/zero";
import { and, eq, inArray, isNull, sql } from "drizzle-orm";
import type { Db } from "../../drizzle";
import type { BrandedFood } from "../branded-food";
import { brandedFood } from "../branded-food/branded-food.sql";
import { OpenFoodFacts } from "../external/openfoodfacts";
import { externalFoodCollection, externalNutrientRef } from "../external/external.sql";
import { Food } from "../food";
import { food, foodMeta } from "../food/food.sql";
import { FoodCategory } from "../food-category";
import { nutrient } from "../nutrient/nutrient.sql";
import { unit } from "../../common/unit/unit.sql";
import { ServingParser } from ".";

export namespace OpenFoodFactsBarcodeIngestion {
  export type BarcodeLookupFailureReason = OpenFoodFacts.BarcodeLookupFailureReason;

  type BarcodeLookupResult = {
    product: unknown;
    reason: "not_found" | "upstream_timeout" | "upstream_error";
  };

  export type BarcodeLookup = (code: string) => Promise<BarcodeLookupResult>;

  export type CreateBrandedFood = (args: {
    tx: Transaction;
    actor: UserActor;
    args: BrandedFood.Create;
    options: { skipCollectionPermissionCheck: true };
  }) => Promise<void>;

  export type LookupByBarcode = (
    db: Db,
    actor: UserActor,
    params: { code: string },
  ) => Promise<Food.Item | null>;

  export async function ingestByBarcode({
    db,
    actor,
    normalizedCode,
    offLookup,
    getOffTaxonomyGraph,
    createBrandedFood,
    lookupByBarcode,
  }: {
    db: Db;
    actor: UserActor;
    normalizedCode: Barcode.Canonical;
    offLookup?: BarcodeLookup;
    getOffTaxonomyGraph?: () => Promise<OpenFoodFacts.CategoryGraph.Snapshot | null>;
    createBrandedFood: CreateBrandedFood;
    lookupByBarcode: LookupByBarcode;
  }): Promise<{ item: Food.Item | null; reason: BarcodeLookupFailureReason }> {
    const [offCollection] = await db
      .select({ id: externalFoodCollection.id })
      .from(externalFoodCollection)
      .where(eq(externalFoodCollection.slug, OpenFoodFacts.slug))
      .limit(1)
      .execute();
    if (!offCollection) {
      return { item: null, reason: "not_found" };
    }

    const units = await db
      .select()
      .from(unit)
      .where(inArray(unit.type, ["mass", "volume"]))
      .execute();
    const nutrientRefs = await db
      .select({
        externalId: externalNutrientRef.externalId,
        id: nutrient.id,
        key: nutrient.key,
        unitKey: nutrient.unitKey,
      })
      .from(externalNutrientRef)
      .innerJoin(nutrient, eq(nutrient.id, externalNutrientRef.nutrientId))
      .where(eq(externalNutrientRef.collectionId, offCollection.id))
      .execute();

    let categoryGraph: OpenFoodFacts.CategoryGraph.Resolver | null = null;
    if (getOffTaxonomyGraph) {
      try {
        const taxonomyGraph = await getOffTaxonomyGraph();
        categoryGraph = taxonomyGraph
          ? OpenFoodFacts.CategoryGraph.createResolver(taxonomyGraph)
          : null;
      } catch {
        categoryGraph = null;
      }
    }

    const resolution = await OpenFoodFacts.resolveBrandedFoodByBarcode({
      code: normalizedCode,
      offBarcodeLookup: offLookup,
      openfoodfactsCollectionId: offCollection.id,
      servingParser: ServingParser.create(units),
      openfoodfactsNutrients: new Map(
        nutrientRefs.map((ref) => [
          ref.externalId,
          { id: ref.id, key: ref.key, unitKey: ref.unitKey },
        ]),
      ),
      ignoreNutrients: new Set(),
      categoryGraph,
    });
    if (!resolution.ok) {
      return { item: null, reason: resolution.reason };
    }

    const transformedInput = resolution.args;
    const canonicalCode = transformedInput.brandedFood.code;
    if (!canonicalCode) {
      return { item: null, reason: "not_found" };
    }
    const parsedCanonicalCode = Barcode.parse(canonicalCode);
    if (!parsedCanonicalCode.ok) {
      return { item: null, reason: "not_found" };
    }
    const canonicalBarcode = parsedCanonicalCode.code;

    await db.zeroTransaction(async (tx) => {
      if (tx.location !== "server") {
        throw new Error("Expected server transaction for OFF barcode ingest");
      }

      const dbTx = tx.dbTransaction.wrappedTransaction;
      const lockKey = `${offCollection.id}:${canonicalCode}`;
      await dbTx.execute(sql`select pg_advisory_xact_lock(hashtext(${lockKey})::bigint)`);

      const [alreadyExists] = await dbTx
        .select({ id: food.id })
        .from(foodMeta)
        .innerJoin(food, Food.currentVersionJoinCondition())
        .innerJoin(
          brandedFood,
          and(eq(brandedFood.id, food.id), eq(brandedFood.version, food.version)),
        )
        .where(
          and(
            eq(foodMeta.type, "branded"),
            eq(foodMeta.collectionId, offCollection.id),
            eq(foodMeta.discoverability, "listed"),
            isNull(foodMeta.timeArchived),
            eq(brandedFood.code, canonicalBarcode),
          ),
        )
        .limit(1)
        .execute();
      if (alreadyExists) {
        return;
      }

      const categoryIds = await FoodCategory.resolveCategoryIdsForKeys(tx, resolution.categoryKeys);
      await createBrandedFood({
        tx,
        actor,
        args: { ...transformedInput, categoryIds },
        options: { skipCollectionPermissionCheck: true },
      });
    });

    const item = await lookupByBarcode(db, actor, { code: canonicalBarcode });
    if (!item) {
      return { item: null, reason: "not_found" };
    }

    return { item, reason: "not_found" };
  }
}
